import React, { useEffect, useState } from 'react';
import { Card, Input, Radio, Button, Space, Typography, Spin, Alert, message, Tag } from 'antd';
import { SaveOutlined, TrophyOutlined, CloseCircleOutlined, QuestionCircleOutlined, LockOutlined } from '@ant-design/icons';
import axiosClient from '@/api/axiosClient';
import { useAuth } from '@/auth/useAuth';

const { Text } = Typography;

type RetrospectiveOutcome = 'WON' | 'LOST' | 'NO_DECISION';

interface RetrospectiveData {
  id?: string;
  outcome?: RetrospectiveOutcome;
  whatWentWell?: string;
  whatToImprove?: string;
  keyTakeaways?: string;
  conductedBy?: string;
  conductedAt?: string;
}

interface RetrospectiveFormProps {
  opportunityId: string;
  onComplete: () => void;
}

const RetrospectiveForm: React.FC<RetrospectiveFormProps> = ({ opportunityId, onComplete }) => {
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole(['BD_MANAGER', 'ADMIN', 'CEO']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState<RetrospectiveData | null>(null);
  const [outcome, setOutcome] = useState<RetrospectiveOutcome>('NO_DECISION');
  const [whatWentWell, setWhatWentWell] = useState('');
  const [whatToImprove, setWhatToImprove] = useState('');
  const [keyTakeaways, setKeyTakeaways] = useState('');

  const fetchRetro = async () => {
    setLoading(true);
    try {
      const response = await axiosClient.get(`/opportunities/${opportunityId}/post-submission/retrospective`);
      if (response.data) {
        setExisting(response.data);
        setOutcome(response.data.outcome || 'NO_DECISION');
        setWhatWentWell(response.data.whatWentWell || '');
        setWhatToImprove(response.data.whatToImprove || '');
        setKeyTakeaways(response.data.keyTakeaways || '');
      }
    } catch {
      // no retrospective yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRetro();
  }, [opportunityId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosClient.post(`/opportunities/${opportunityId}/post-submission/retrospective`, {
        outcome,
        whatWentWell: whatWentWell.trim() || undefined,
        whatToImprove: whatToImprove.trim() || undefined,
        keyTakeaways: keyTakeaways.trim() || undefined,
      });
      message.success('Retrospective saved');
      await fetchRetro();
      onComplete();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to save retrospective');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card size="small" style={{ marginTop: 12 }}>
        <Spin size="small" /> Loading retrospective...
      </Card>
    );
  }

  const isComplete = Boolean(existing?.outcome);

  const outcomeTag = (o: RetrospectiveOutcome) => {
    if (o === 'WON') return <Tag icon={<TrophyOutlined />} color="success">Won</Tag>;
    if (o === 'LOST') return <Tag icon={<CloseCircleOutlined />} color="error">Lost</Tag>;
    return <Tag icon={<QuestionCircleOutlined />} color="default">No Decision</Tag>;
  };

  return (
    <Card
      size="small"
      title={<Text strong style={{ fontSize: 14 }}>Phase H: Retrospective</Text>}
      style={{ marginTop: 12, borderColor: isComplete ? 'var(--color-success)' : 'var(--color-warning)' }}
    >
      {!canEdit && (
        <Alert
          message={<span><LockOutlined /> Read-only — only the BD Manager, Admin, or CEO can record the retrospective.</span>}
          type="info"
          showIcon={false}
          style={{ marginBottom: 12 }}
        />
      )}
      {!isComplete && (
        <Alert
          message="Recording an outcome and retrospective is required to complete Phase H"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {isComplete && (
        <Space style={{ marginBottom: 12 }}>
          <Text type="secondary">Outcome:</Text>
          {outcomeTag(existing!.outcome!)}
          {existing!.conductedBy && (
            <Text type="secondary">Conducted by {existing!.conductedBy}</Text>
          )}
        </Space>
      )}
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Outcome <span style={{ color: 'var(--color-error)' }}>*</span>
          </Text>
          <Radio.Group value={outcome} onChange={(e) => setOutcome(e.target.value)}>
            <Radio.Button value="WON">
              <TrophyOutlined style={{ color: 'var(--color-success)' }} /> Won
            </Radio.Button>
            <Radio.Button value="LOST">
              <CloseCircleOutlined style={{ color: 'var(--color-error)' }} /> Lost
            </Radio.Button>
            <Radio.Button value="NO_DECISION">
              <QuestionCircleOutlined /> No Decision
            </Radio.Button>
          </Radio.Group>
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>What Went Well</Text>
          <Input.TextArea
            value={whatWentWell}
            onChange={(e) => setWhatWentWell(e.target.value)}
            placeholder="Strengths, successes, positive outcomes..."
            rows={3}
          />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>What to Improve</Text>
          <Input.TextArea
            value={whatToImprove}
            onChange={(e) => setWhatToImprove(e.target.value)}
            placeholder="Areas for improvement, challenges faced..."
            rows={3}
          />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Key Takeaways</Text>
          <Input.TextArea
            value={keyTakeaways}
            onChange={(e) => setKeyTakeaways(e.target.value)}
            placeholder="Lessons learned, recommendations for future proposals..."
            rows={3}
          />
        </div>
        <div style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!canEdit}
          >
            {isComplete ? 'Update Retrospective' : 'Save Retrospective'}
          </Button>
        </div>
      </Space>
    </Card>
  );
};

export default RetrospectiveForm;
